/**
 * The calendar model: every overpass over the viewport, with the run's marks on top.
 *
 * This is the third imagery concept of docs/MAP_IMAGERY_RESEARCH.md §2, and it is a
 * LABELLED SUBSET of the second, not a separate source. So the list is a union:
 *
 *   * a date the viewport reports  — imagery exists, whether or not we ever scored it;
 *   * a date the run reports       — we scored it, and either accepted it or wrote down why
 *                                    not. It can be missing from the viewport list, because
 *                                    that list is pre-filtered for cloud and a rejected date
 *                                    is usually a cloudy one. Dropping those would hide
 *                                    exactly the dates worth explaining.
 *
 * Three marks, and the third one matters: an overpass we simply never scored is not the
 * same statement as a rejection. On the eight Ukrainian fields 28 observations of 120 passed
 * QA, and until now that number lived in psql.
 *
 * Pure: no React, no fetching. The counts are what the summary line reads.
 */

import type { AuditRow, PreviewDate } from "../api/types";
import { describeRejection } from "./strip";
import { collapseByDate } from "./timeline";

export type DayMark = "accepted" | "rejected" | "unscored";

export interface CalendarDay {
  date: string;
  mark: DayMark;
  /** The rejection in words. Null unless `mark === "rejected"`. */
  reason: string | null;
  ndvi: number | null;
  /** Reported by the viewport list, i.e. imagery is known to be cloud-light enough to look at. */
  available: boolean;
  /** "05" — the day of the month, which is all a dense grid has room for. */
  label: string;
  /**
   * Inside the run's 60-day window. Days outside it are dimmed, so it is visible that the
   * state strip is a SUBSET of the calendar and not a second opinion about the same dates.
   * True everywhere when no window is known — nothing to contrast with.
   */
  inRunWindow: boolean;
}

export interface CalendarMonth {
  key: string;
  label: string;
  days: CalendarDay[];
}

export interface CalendarModel {
  months: CalendarMonth[];
  days: CalendarDay[];
  total: number;
  accepted: number;
  rejected: number;
  unscored: number;
  /** The run's window, when a run is on screen: the strip's own span, drawn as context. */
  window: RunWindow | null;
}

/** The 60-day window a run looked at — `strip.windowStart` / `windowEnd`. */
export interface RunWindow {
  start: string;
  end: string;
}

const monthFormat = new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" });

/** Parsed as UTC so a date never slides to the previous day west of Greenwich. */
const asUtc = (iso: string): Date | null => {
  const parsed = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isIsoDate = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && asUtc(value) !== null;

/**
 * One vocabulary of rejection reasons for the whole interface: the calendar says exactly
 * what the rejected-dates log says, because both call this. A preview date carries the
 * reason under a shorter name and without the fractions, so the sentence comes out without
 * the percentages the log can show.
 */
const reasonWords = (entry: PreviewDate): string => {
  const row: AuditRow = {
    acquisition_id: "",
    date: entry.date,
    rejection_reason: entry.reject_reason ?? null,
  };
  return describeRejection(row);
};

export const buildCalendar = (
  viewportDates: readonly string[],
  runDates: readonly PreviewDate[],
  window: RunWindow | null = null,
): CalendarModel => {
  // "Accepted wins the slot" now lives in lib/timeline.ts, because the state strip needs
  // exactly the same rule and used not to have it.
  const marks = new Map<string, PreviewDate>();
  for (const entry of collapseByDate(
    runDates.filter((entry) => isIsoDate(entry?.date)),
    (entry) => entry.date,
    (entry) => Boolean(entry.accepted),
  )) {
    marks.set(entry.date, entry);
  }

  const available = new Set(viewportDates.filter(isIsoDate));
  const all = [...new Set([...available, ...marks.keys()])].sort();
  const inWindow = (date: string): boolean =>
    window === null || (date >= window.start && date <= window.end);

  const days: CalendarDay[] = all.map((date) => {
    const mark = marks.get(date);
    const day = date.slice(8, 10);
    if (!mark) {
      return {
        date,
        mark: "unscored",
        reason: null,
        ndvi: null,
        available: true,
        label: day,
        inRunWindow: inWindow(date),
      };
    }
    return {
      date,
      mark: mark.accepted ? "accepted" : "rejected",
      reason: mark.accepted ? null : reasonWords(mark),
      ndvi: typeof mark.ndvi_mean === "number" ? mark.ndvi_mean : null,
      available: available.has(date),
      label: day,
      inRunWindow: inWindow(date),
    };
  });

  const months: CalendarMonth[] = [];
  for (const day of days) {
    const key = day.date.slice(0, 7);
    const last = months.at(-1);
    if (last?.key === key) last.days.push(day);
    else months.push({ key, label: monthFormat.format(asUtc(day.date) as Date), days: [day] });
  }

  return {
    months,
    days,
    total: days.length,
    accepted: days.filter((day) => day.mark === "accepted").length,
    rejected: days.filter((day) => day.mark === "rejected").length,
    unscored: days.filter((day) => day.mark === "unscored").length,
    window,
  };
};

export const dayOf = (model: CalendarModel, date: string | null): CalendarDay | null =>
  date === null ? null : (model.days.find((day) => day.date === date) ?? null);
